import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Alert, Stack, TextField, Typography } from '@mui/material';

import { getAnswersSchoolFeeding, putAnswers } from 'api/answers/answers.api';
import {
  FormName,
  GetAnswersPayload,
  PostAnswersPayload,
} from 'api/answers/dtos/answers.dto';
import { ListProducts } from 'components/cifa/list_products';
import { ContainerForm } from 'components/shared/container_form';
import { FormButton, FormButtonType } from 'components/shared/form_button';
import { HorizontalStepper } from 'components/shared/horizontal_stepper';
import { Modal } from 'components/shared/modal';
import { PageTitle } from 'components/shared/page_title';
import { SnackbarFeedback } from 'components/shared/snackbar_feedback';
import { ROUTES } from 'routes/constants';
import { NumericFormat } from 'react-number-format';
import ContainerPainel from 'components/container_painel';

export interface Product {
  name: string;
  identification: string;
  value: string;
}

export const formatCpf = (cpf: string): string => {
  const cleanedCpf = cpf.replace(/\D/g, '');
  return cleanedCpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
};

export const formatCnpj = (value?: string): string => {
  if (!value) return '';
  const digitsOnly = value.replace(/\D/g, '');
  return digitsOnly.replace(
    /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/,
    '$1.$2.$3/$4-$5',
  );
};

// pure helper to add or update a product in a list
export const upsertProduct = (
  list: Product[],
  product: Product,
): Product[] => {
  const idx = list.findIndex((p) => p.identification === product.identification);
  if (idx !== -1) {
    const copy = [...list];
    copy[idx] = product;
    return copy;
  }
  return [...list, product];
};

export const deleteProduct = (list: Product[], id: string): Product[] =>
  list.filter((p) => p.identification !== id);

// exported helpers wrapping pure operations so they can be unit tested
export const handleCnpjProductHelper = (list: Product[], product: Product): Product[] =>
  upsertProduct(list, product);

export const handleCpfProductHelper = (list: Product[], product: Product): Product[] =>
  upsertProduct(list, product);

export const handleDeleteCnpjProductHelper = (list: Product[], id: string): Product[] =>
  deleteProduct(list, id);

export const handleDeleteCpfProductHelper = (list: Product[], id: string): Product[] =>
  deleteProduct(list, id);

const EditStateSchoolFeeding2025: React.FC = () => {
  const navigate = useNavigate();

  const [provided, setProvided] = useState<string>('');
  const [applied, setApplied] = useState<string>('');

  const [errorMessage, setErrorMessage] = useState<string>();
  const [loading, setLoading] = useState<boolean>();

  const [addModal, setAddModal] = useState<string | undefined>(undefined);
  const [name, setName] = useState<string>();
  const [identification, setidentification] = useState<string>();
  const [value, setValue] = useState<string>('');
  const [cnpjProducts, setCnpjProducts] = useState<Product[]>([]);
  const [cpfProducts, setCpfProducts] = useState<Product[]>([]);
  const [id, setId] = useState<string>();

  const handleCnpjProduct = () => {
    if (name && identification && value) {
      const product: Product = {
        name,
        identification,
        value,
      };

      setCnpjProducts((prevProducts) =>
        handleCnpjProductHelper(prevProducts, product),
      );

      setAddModal(undefined);
      setName(undefined);
      setValue('');
      setidentification(undefined);
    }
  };

  const handleCpfProduct = () => {
    if (name && identification && value) {
      const product: Product = {
        name,
        identification,
        value,
      };

      setCpfProducts((prevProducts) =>
        handleCpfProductHelper(prevProducts, product),
      );

      setAddModal(undefined);
      setName(undefined);
      setValue('');
      setidentification(undefined);
    }
  };

  const handleDeleteCnpjProduct = (id: string) => {
    setCnpjProducts((prevProducts) =>
      handleDeleteCnpjProductHelper(prevProducts, id),
    );

    setAddModal(undefined);
    setName(undefined);
    setValue('');
    setidentification(undefined);
  };

  const handleDeleteCpfProduct = (id: string) => {
    setCpfProducts((prevProducts) =>
      handleDeleteCpfProductHelper(prevProducts, id),
    );

    setAddModal(undefined);
    setName(undefined);
    setValue('');
    setidentification(undefined);
  };

  const handleSubmit = async () => {
    setLoading(true);

    const institutionUuid = localStorage.getItem('institutionUuid') ?? '';
    const userAccessUuid = localStorage.getItem('userAccessUuid') ?? '';

    const payload: PostAnswersPayload = {
      userAccessUuid,
      institutionUuid,
      formName: FormName.ALIMENTACAO_ESCOLAR_ESTADUAL,
      answer: {
        valueProvidedByPnae: parseFloat(provided.replace(/[^\d.-]/g, '')) || 0,
        valueAppliedByPnaeResource:
          parseFloat(applied?.replace(/[^\d.-]/g, '')) || 0,
        productAquiredFromCnpjByPnaeResourceList: cnpjProducts,
        productAquiredFromCpfByPnaeResourceList: cpfProducts,
      },
      referencedYear: 2025,
    };

    if (id) {
      try {
        await putAnswers(payload, id);
        navigate(ROUTES.EDIT_MUNICIPAL_SCHOOL_FEEDING_2025);
      } catch (error) {
        setErrorMessage(
          'Erro ao atualizar os dados da Alimentação Escolar (PNAE Estadual).',
        );
      } finally {
        setLoading(false);
      }
    } else {
      setErrorMessage(
        'Erro ao atualizar os dados da Alimentação Escolar (PNAE Estadual). ',
      );
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      const userAccessUuid = localStorage.getItem('userAccessUuid') ?? '';
      const institutitonUuid = localStorage.getItem('institutionUuid') ?? '';
      const payload: GetAnswersPayload = {
        userAccessUuid,
        institutitonUuid,
        referencedYear: 2025,
      };

      try {
        const response = await getAnswersSchoolFeeding(payload) as { data: any[] };

        const form = response?.data.find(
          (form: any) => form.formName === 'ALIMENTAÇÃO ESCOLAR ESTADUAL',
        );
        if (form) {
          setId(form?.uuid);
          setApplied(form?.answer?.valueAppliedByPnaeResource);
          setProvided(form?.answer?.valueProvidedByPnae);
          setCnpjProducts(
            form?.answer?.productAquiredFromCnpjByPnaeResourceList,
          );
          setCpfProducts(form?.answer?.productAquiredFromCpfByPnaeResourceList);
        }
      } catch (error) {
        setErrorMessage(
          'Erro ao recuperar os dados da Aba Alimentação Escolar (Estadual).',
        );
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  return (
    <ContainerPainel
      pagina='CIFA - Editar Município - 2025'
      showBackButton={true}
      backButtonPath={ROUTES.CIFA2025}
    >
      <HorizontalStepper activeStep={2} />

      <PageTitle
        title='Alimentação Escolar (PNAE Estadual)'
        subtitle='Informe corretamente os dados sobre a aquisição de Alimentação Escolar através do PNAE Estadual no Município.'
      />

      <Stack marginX='4rem'>
        <ContainerForm title='Valores para Aquisição de Alimentação Escolar - Em 2024'>
          <Stack direction='row' justifyContent='space-between' gap='1rem'>
            <NumericFormat
              id='disponibilizado'
              label='Valor disponibilizado pelo PNAE Federal em R$:'
              value={provided}
              onValueChange={(values) => setProvided(values.value)}
              thousandSeparator='.'
              decimalSeparator=','
              prefix='R$ '
              customInput={TextField}
              InputLabelProps={{ shrink: true }}
              sx={{ flex: 1 }}
            />
            <NumericFormat
              id='aplicado'
              label='Valores totais aplicados pelo município na Alimentação Escolar oriundos de todo e qualquer fornecedor no ano de 2024 '
              value={applied}
              onValueChange={(values) => setApplied(values.value)}
              thousandSeparator='.'
              decimalSeparator=','
              prefix='R$ '
              customInput={TextField}
              InputLabelProps={{ shrink: true }}
              sx={{ flex: 1 }}
            />
          </Stack>
        </ContainerForm>

        <ContainerForm title='Produtos adquiridos via CNPJ (com recurso do PNAE Estadual) - Valores Aplicados na Aquisição de Produtos (Opcional)'>
          {cnpjProducts.length === 0 && (
            <Alert
              severity='info'
              sx={{ backgroundColor: '#EAEFF9', my: '2rem' }}
            >
              <Typography
                variant='body2'
                fontSize={14}
                lineHeight={1.5}
                textAlign='left'
              >
                Você ainda não realizou a inclusão de nenhuma Aquisição de
                Produto via CNPJ. Clique no botão abaixo ”Adicionar Aquisição”
                para incluir uma Aquisição de Produto.
              </Typography>
            </Alert>
          )}

          <FormButton
            title='+ Adicionar Aquisição'
            type={FormButtonType.PRIMARY}
            onClick={() => setAddModal('CNPJ')}
          />

          {cnpjProducts.length > 0 && (
            <ListProducts
              products={cnpjProducts}
              documentType='CNPJ'
              edit={(value) => {
                setAddModal('CNPJ');
                setValue(value.value);
                setName(value.name);
                setidentification(value.identification);
              }}
              remove={(value) => handleDeleteCnpjProduct(value.identification)}
            />
          )}
        </ContainerForm>

        <ContainerForm title='Produtos adquiridos via CPF (com recurso do PNAE Estadual) - Valores Aplicados na Aquisição de Produtos (Opcional)'>
          {cpfProducts.length === 0 && (
            <Alert
              severity='info'
              sx={{ marginY: '2rem', backgroundColor: '#EAEFF9' }}
            >
              <Typography
                variant='body2'
                fontSize='14px'
                textAlign='left'
                lineHeight={1.5}
              >
                Você ainda não realizou a inclusão de nenhuma Aquisição de
                Produto via CPF. Clique no botão abaixo ”Adicionar Aquisição”
                para incluir uma Aquisição de Produto.
              </Typography>
            </Alert>
          )}

          <FormButton
            type={FormButtonType.PRIMARY}
            title='+ Adicionar Aquisição'
            onClick={() => setAddModal('CPF')}
          />

          {cpfProducts.length > 0 && (
            <ListProducts
              products={cpfProducts}
              documentType={'CPF'}
              edit={(value) => {
                setAddModal('CPF');
                setValue(value.value);
                setName(value.name);
                setidentification(value.identification);
              }}
              remove={(value) => handleDeleteCpfProduct(value.identification)}
            />
          )}
        </ContainerForm>

        <Stack
          direction={{ md: 'row-reverse' }}
          marginY='2rem'
          sx={{ gap: '1rem' }}
        >
          <FormButton
            title='Avançar'
            type={FormButtonType.PRIMARY}
            onClick={handleSubmit}
            isDisabled={loading}
          />
          <FormButton
            title='Voltar'
            type={FormButtonType.SECONDARY}
            onClick={() => {
              setAddModal(undefined);
              setValue('');
              setName(undefined);
              setidentification(undefined);
              navigate(ROUTES.EDIT_FEDERAL_SCHOOL_FEEDING_2025);
            }}
          />
        </Stack>

        <Modal
          open={!!addModal}
          onClose={() => setAddModal(undefined)}
          title={`Adicionar Aquisição via ${addModal} - Produtos adquiridos com recurso do PNAE Estadual`}
          buttons={[
            <FormButton
              key={1}
              type={FormButtonType.SECONDARY}
              title='Voltar'
              onClick={() => setAddModal(undefined)}
            />,
            <FormButton
              key={0}
              type={FormButtonType.PRIMARY}
              title='Confirmar'
              onClick={() =>
                addModal === 'CNPJ' ? handleCnpjProduct() : handleCpfProduct()
              }
            />,
          ]}
          directionButtons='row'
        >
          <>
            <TextField
              id='nome'
              variant='outlined'
              label='Nome/Razão social'
              value={name}
              onChange={(e) => setName(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <Stack
              mt='1rem'
              direction='row'
              gap='1rem'
              justifyContent='space-between'
            >
              <TextField
                id='documento'
                variant='outlined'
                label={addModal}
                value={
                  addModal === 'CPF'
                    ? formatCpf(identification as string)
                    : formatCnpj(identification as string)
                }
                onChange={(e) =>
                  setidentification(e.target.value.replace(/\D/g, ''))
                }
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  maxLength: addModal === 'CPF' ? 14 : 18,
                }}
                sx={{ flexGrow: 1 }}
              />
              <NumericFormat
                id='valor'
                label='Valor'
                variant='outlined'
                customInput={TextField}
                value={value}
                onChange={(e) => setValue(e.target.value.replace(/\D/g, ''))}
                thousandSeparator='.'
                decimalSeparator=','
                prefix='R$ '
                InputLabelProps={{ shrink: true }}
                sx={{ flexGrow: 1 }}
              />
            </Stack>
          </>
        </Modal>

        {errorMessage && (
          <SnackbarFeedback isOpen type='error' message={errorMessage} />
        )}
      </Stack>
    </ContainerPainel>
  );
};

export default EditStateSchoolFeeding2025;
